import { useEffect, useRef, useState } from 'react';
import { Button } from '../../typography/button/button';

declare global {
    interface Window {
        OneTrust: {
            initializeCookiePolicyHtml: () => void;
            ToggleInfoDisplay: () => void;
        };
    }
}

export type OneTrustDisplayType = 'cookie-policy' | 'cookie-settings-button';

export type OneTrustProps = {
    displayType?: OneTrustDisplayType;
};

const CookiePolicy = () => {
    const [oneTrustLoaded, setOneTrustLoaded] = useState(false);
    const divRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const initializeOneTrust = () => {
            if (typeof window !== 'undefined' && 'OneTrust' in window) {
                setOneTrustLoaded(true);
                if (divRef.current && !divRef.current.querySelector('#ot-sdk-cookie-policy')) {
                    divRef.current.insertAdjacentHTML('afterbegin', '<div id="ot-sdk-cookie-policy"></div>');
                    window.OneTrust.initializeCookiePolicyHtml();
                }
            }
        };
        initializeOneTrust();
        // Check again after a short delay in case OneTrust loads after component mounts
        const timeout = setTimeout(initializeOneTrust, 1000);
        return () => clearTimeout(timeout);
    }, []);

    return (
        <div data-component="onetrust-cookie-policy" suppressHydrationWarning={true} ref={divRef}>
            {!oneTrustLoaded && <p className="r-italic r-text-gray-500">Cookie policy will be displayed here when OneTrust loads.</p>}
        </div>
    );
};

const CookieSettingsButton = () => {
    const handleClick = () => {
        if (typeof window !== 'undefined' && window.OneTrust?.ToggleInfoDisplay) {
            window.OneTrust.ToggleInfoDisplay();
        }
    };

    return (
        <Button className="ot-sdk-show-settings" size="small" onClick={handleClick}>
            Cookie Settings
        </Button>
    );
};

export const OneTrust = ({ displayType = 'cookie-policy' }: OneTrustProps) => {
    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        setIsClient(true);
    }, []);

    // Only render on client to avoid hydration mismatch - OneTrust SDK populates these divs with content
    if (!isClient) {
        return null;
    }

    switch (displayType) {
        case 'cookie-settings-button':
            return <CookieSettingsButton />;
        case 'cookie-policy':
        default:
            return <CookiePolicy />;
    }
};
